import CloseIcon from '@mui/icons-material/Close';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import CommonDialog from '../../../../common/CommonDialog';
import { INVENTORY_MANAGE_TYPE, PRICING_TYPES } from '../../../../utility/Constant';
import useCheckExistService from '../SavedServicesAddModal/useCheckExistService';
import VehicleMakeSelection from '../SavedServicesAddModal/VehicleMakeSelection';

const CreateSavedServiceFromInvoice = ({ open, onClose, onSave, loader = false, vehicleMake = [], initialVehicleMake = null, serviceData = null }) => {
    const [shopReference, setShopReference] = useState('');
    // const [selectedMakes, setSelectedMakes] = useState(initialVehicleMake ? [initialVehicleMake] : []);
    const [selectedMakes, setSelectedMakes] = useState([{ _id: 'all', name: 'All' }, ...vehicleMake]);
    const [showOverrideModal, setShowOverrideModal] = useState(false);

    // Use the same hook as SavedServicesAddModal
    const { existingServiceData, shopServiceReferenceError, isCheckingExistence, checkServiceExistence, resetExistenceCheck } = useCheckExistService();

    // Check service existence when service, vehicle makes, or shop reference changes
    useEffect(() => {
        if (serviceData?.service?._id && selectedMakes?.length > 0) {
            // Create a service object in the format expected by checkServiceExistence
            const selectedServiceObj = {
                _id: serviceData?.service?._id,
                serviceSubCategory: {
                    _id: serviceData?.service?._id,
                    name: serviceData?.service?.name,
                },
            };

            checkServiceExistence(null, selectedServiceObj, selectedMakes, shopReference);
        } else {
            resetExistenceCheck();
        }
    }, [serviceData?.service?._id, selectedMakes, shopReference, checkServiceExistence, resetExistenceCheck]);

    // Function to build the payload
    const buildPayload = (shouldOverride = false) => {
        if (!serviceData) {
            return null;
        }

        // Handle vehicle make - check if "All" is selected
        const isAllSelected = selectedMakes?.some((make) => make._id === 'all');
        const vehicleMakeIds = isAllSelected ? ['all'] : selectedMakes?.map((make) => make._id) || [];

        // Base payload
        const payload = {
            service: serviceData?.service?._id,
            serviceCategory: serviceData?.serviceCategory?._id || serviceData?.service?.serviceCategory?._id,
            pricingType: serviceData?.pricingType,
            vehicleMake: vehicleMakeIds,
            shouldOverride,
            isGroup: true,
        };

        if (shopReference) {
            payload.shopServiceReference = shopReference;
        }
        if (serviceData?.optionalDescription) {
            payload.description = serviceData?.optionalDescription;
        }

        // Add pricing type specific fields
        if (serviceData?.pricingType === PRICING_TYPES.FXIED) {
            // Fixed pricing type
            payload.fixedPrice = Number(serviceData?.fixedPrice) || 0;
        } else if (serviceData?.pricingType === PRICING_TYPES.LABOUR) {
            // Labour pricing type
            if (Number(serviceData.qty) > 0 && Number(serviceData.ratePerHour) > 0) {
                payload.labor = {
                    qty: Number(serviceData?.qty),
                    price: Number(serviceData?.ratePerHour),
                };
            }

            // Add parts data if available (only unmanaged parts)
            if (serviceData?.parts && serviceData.parts.length > 0) {
                payload.parts = serviceData.parts
                    .filter((part) => !part.inventoryType || part.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED)
                    .map((part) => ({
                        inventoryType: part.inventoryType || INVENTORY_MANAGE_TYPE.UNMANAGED,
                        partName: part.partName,
                        qty: Number(part.partQty) || 1,
                        price: Number(part.partCost) || 0,
                    }));
            }
        }

        return payload;
    };

    // Function to perform the actual save operation
    const performSave = (shouldOverride = false) => {
        const payload = buildPayload(shouldOverride);
        if (payload) {
            onSave(payload);
        }
        setShowOverrideModal(false);
    };

    const handleSave = () => {
        if (!serviceData) {
            console.error('No service data provided');
            return;
        }

        // Validate vehicle make selection
        if (!selectedMakes || selectedMakes.length === 0) {
            toast.error('Please select at least one vehicle make');
            return;
        }

        // Validate shop reference (now required)
        if (!shopReference || shopReference.trim() === '') {
            toast.error('Shop reference is required');
            return;
        }

        // Continue with normal flow - no need to check for multiple makes since we create single record
        proceedWithConflictCheck();
    };

    // Proceed with conflict check
    const proceedWithConflictCheck = () => {
        // Check for shop service reference conflict
        // if (shopServiceReferenceError) {
        //     toast.error('A service with the same shop reference already exists.');
        //     return;
        // }

        // Check if there's an existing service conflict
        if (existingServiceData?.savedServiceExist) {
            setShowOverrideModal(true);
            return;
        }

        // If no conflict, proceed with normal save
        performSave(false);
    };

    // Handle override modal actions
    const handleOverrideCancel = () => {
        setShowOverrideModal(false);
    };

    const handleOverrideConfirm = () => {
        performSave(true);
    };

    const handleClose = () => {
        setShopReference('');
        setSelectedMakes([{ _id: 'all', name: 'All' }]);
        setShowOverrideModal(false);
        resetExistenceCheck();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose();
                }
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    maxWidth: '470px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: '#000!important',
                    display: 'flex',
                    borderBottom: '1px solid #0000001F',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: 'white',
                    padding: '16px!important',
                }}
            >
                <Typography variant="h6" width={'90%'} color={'#000000DE'}>
                    Save service?
                </Typography>
                <Stack direction={'row'} spacing={0}>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon sx={{ fill: '#000000DE', fontSize: 32 }} />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: '16px',
                    marginTop: '16px',
                }}
            >
                <Typography variant="body1" sx={{ marginBottom: '24px', color: 'rgba(0, 0, 0, 0.87)' }}>
                    Would you like to save this service to your shop settings? It will be available for future use in estimates, and invoices.
                </Typography>

                <Stack spacing={3}>
                    {/* Shop Reference Input */}
                    <Stack spacing={1}>
                        <Typography variant="h6" fontSize={16}>
                            Shop Reference<span className="asterik">*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter shop reference"
                            value={shopReference}
                            onChange={(e) => setShopReference(e.target.value)}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '50px',
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Stack>

                    {/* Vehicle Make Selection */}
                    <VehicleMakeSelection vehicleMake={vehicleMake} selectedMakes={selectedMakes} setFormSelectedMakes={setSelectedMakes} />
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    padding: '16px 8px',
                    gap: 1,
                }}
            >
                <Button onClick={handleClose} variant="outlined" disabled={loader}>
                    CANCEL
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={loader || isCheckingExistence} sx={{ width: '86px' }}>
                    {loader ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'SAVE'}
                </Button>
            </DialogActions>

            {/* Override Confirmation Modal */}
            <CommonDialog
                dialogClaassName="commonDialog commonDialog__600"
                open={showOverrideModal}
                onClose={handleOverrideCancel}
                onSave={handleOverrideConfirm}
                loader={loader}
                dialogTitle="⚠️ Overwrite saved service? "
                dialogSubtitle="Overwriting will replace the existing saved service with the new parts, prices, and hours you've entered."
                cancelButtonText="CANCEL"
                saveButtonText="SAVE"
                saveButtonType="primary"
                cancelButtonWidth="104px"
                saveButtonWidth="82px"
            />
        </Dialog>
    );
};

export default CreateSavedServiceFromInvoice;
